/**
 * Country utility functions for slug generation and flag emoji conversion.
 * Used across the app to map between country names, codes, slugs, and flags.
 */

/**
 * Convert a country name to a URL-friendly slug.
 * e.g. "United States" → "united-states", "Côte d'Ivoire" → "cote-divoire"
 */
export function nameToSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/['']/g, '')            // Remove apostrophes
    .replace(/[^a-z0-9]+/g, '-')    // Replace non-alphanumeric with hyphens
    .replace(/^-+|-+$/g, '');        // Trim leading/trailing hyphens
}

/**
 * Get the URL for a country flag image (cross-browser compatible).
 * Uses flagcdn.com which provides PNG flags by ISO country code.
 */
export function codeToFlagUrl(code: string, width = 80): string {
  return `https://flagcdn.com/w${width}/${code.toLowerCase()}.png`;
}

/**
 * Convert a slug back to find a matching country name/code.
 * Looks up the slug against all countries from the database.
 */
export function findCountryBySlug(
  slug: string,
  countries: Array<{ code: string; name: string }>
): { code: string; name: string } | null {
  for (const country of countries) {
    if (nameToSlug(country.name) === slug) {
      return {
        code: country.code,
        name: country.name,
      };
    }
  }
  return null;
}
